// Thin fetch wrapper for the Live Timing Configurator backend
// (backend/live_timing_config_routes.py). Unlike the public pages, admin
// pages DO call a backend API directly - live-frontend's nginx has no
// /api/ proxy (see nginx.conf), so this hits the backend's own origin,
// relying on Flask-CORS (backend/app.py, CORS_ORIGINS env var) rather than
// same-origin proxying.
const API_BASE = import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:5000'

const SESSION_KEY = 'live-timing-config-session'

export function getSession() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null')
  } catch {
    return null
  }
}

export function setSession(session) {
  if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  else localStorage.removeItem(SESSION_KEY)
}

async function request(path, { method = 'GET', body, auth = true } = {}) {
  const headers = { 'Content-Type': 'application/json' }
  if (auth) {
    const session = getSession()
    if (session?.session_token) headers['Authorization'] = `Bearer ${session.session_token}`
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status})`)
    err.status = res.status
    throw err
  }
  return data
}

export function requestLoginLink(email) {
  return request('/api/live-timing-config/login', { method: 'POST', body: { email }, auth: false })
}

export function verifyToken(token) {
  return request(`/api/live-timing-config/verify?token=${encodeURIComponent(token)}`, { auth: false })
}

export function logout() {
  return request('/api/live-timing-config/logout', { method: 'POST' }).finally(() => setSession(null))
}

export function listSessions(hill, date) {
  return request(`/api/live-timing-config/sessions?hill=${encodeURIComponent(hill)}&date=${encodeURIComponent(date)}`)
}

export function updateSession(hill, date, payload) {
  return request(`/api/live-timing-config/sessions?hill=${encodeURIComponent(hill)}&date=${encodeURIComponent(date)}`,
    { method: 'PUT', body: payload })
}

export function resetSession(hill, date, sessionCode) {
  return request(`/api/live-timing-config/sessions?hill=${encodeURIComponent(hill)}&date=${encodeURIComponent(date)}`,
    { method: 'DELETE', body: { session_code: sessionCode } })
}

export function listRuns(hill, date, raceNumber) {
  let path = `/api/live-timing-config/runs?hill=${encodeURIComponent(hill)}&date=${encodeURIComponent(date)}`
  if (raceNumber !== undefined && raceNumber !== null && raceNumber !== '') {
    path += `&race_number=${encodeURIComponent(raceNumber)}`
  }
  return request(path)
}

export function updateRun(hill, date, payload) {
  return request(`/api/live-timing-config/runs?hill=${encodeURIComponent(hill)}&date=${encodeURIComponent(date)}`,
    { method: 'PUT', body: payload })
}

export function listNonEntrantRuns(hill, date) {
  return request(`/api/live-timing-config/runs/non-entrants?hill=${encodeURIComponent(hill)}&date=${encodeURIComponent(date)}`)
}

export function deleteNonEntrantRuns(hill, date) {
  return request(`/api/live-timing-config/runs/non-entrants?hill=${encodeURIComponent(hill)}&date=${encodeURIComponent(date)}`,
    { method: 'DELETE' })
}

export function clearDown() {
  return request('/api/live-timing-config/clear-down', { method: 'POST' })
}

export function sendResultsEmail(hill, date) {
  return request('/api/live-timing-config/results-email/send', { method: 'POST', body: { hill, date } })
}

export function listReplayEvents() {
  return request('/api/live-timing-config/replay/events')
}

export function startReplay(hill, date, intervalSeconds) {
  return request('/api/live-timing-config/replay/start', {
    method: 'POST', body: { hill, date, interval_seconds: intervalSeconds },
  })
}

export function stopReplay() {
  return request('/api/live-timing-config/replay/stop', { method: 'POST' })
}

export function getReplayStatus() {
  return request('/api/live-timing-config/replay/status')
}
